import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  AlertTriangle,
  Sparkles,
  GraduationCap,
  Target,
  Wrench,
  HeartHandshake,
  Briefcase,
  Building2,
  Code2,
  Layers,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  educationHero,
  trainingProblems,
  trainingApproach,
  trainingFormats,
  curriculumCore,
  curriculumRoles,
  audienceFit,
  trainingProcess,
} from '../mock';

const SectionEyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#C2603A]">
    <span className="w-5 h-px bg-[#C2603A]/60" /> {children} <span className="w-5 h-px bg-[#C2603A]/60" />
  </div>
);

const formatIcons = [Briefcase, Building2, Code2, Layers];
const approachIcons = [Wrench, Target, HeartHandshake];

const Education = () => {
  return (
    <main className="bg-cream sans-body text-ink">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative mx-auto max-w-5xl px-5 pt-16 md:pt-24 pb-20 md:pb-28 text-center fade-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-ink-10 px-4 py-1.5 text-[12px] text-muted">
            <Sparkles className="w-3.5 h-3.5 text-[#C2603A]" /> AI Transformation Partner · 2026
          </div>
          <div className="mt-6">
            <SectionEyebrow>{educationHero.eyebrow}</SectionEyebrow>
          </div>
          <h1 className="serif-display text-[44px] sm:text-6xl md:text-7xl leading-[1.05] mt-4 text-ink">
            Upskill Your Team. <span className="italic text-[#C2603A]">Actually.</span>
          </h1>
          <div className="mx-auto mt-8 h-[3px] w-16 rounded-full bg-[#C2603A]/80" />
          <p className="mx-auto mt-8 max-w-2xl text-[17px] md:text-[18px] leading-relaxed text-muted">
            {educationHero.description}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to={educationHero.primaryCta.href}
              className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-medium"
            >
              {educationHero.primaryCta.label} <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={educationHero.secondaryCta.href}
              className="btn-outline inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-medium"
            >
              {educationHero.secondaryCta.label}
            </a>
          </div>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>The Problem</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">Why Most AI Training Fails</h2>
            <p className="mt-4 text-muted max-w-2xl mx-auto">
              Generic training doesn&apos;t work because every company operates differently
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-12">
            {trainingProblems.map((p) => (
              <article key={p.title} className="rounded-2xl bg-white border border-ink-10 p-6 soft-card">
                <div className="w-10 h-10 rounded-xl bg-[#FCE7D6] text-[#C2603A] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <h3 className="serif-display text-2xl mt-4">{p.title}</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-muted">{p.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="relative py-20 md:py-28 bg-[#F6EFE4]">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>Our Approach</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">How We Train Differently</h2>
            <p className="mt-4 text-muted max-w-2xl mx-auto">
              Custom curricula built around your workflows, tools, and actual business challenges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {trainingApproach.map((a, i) => {
              const Icon = approachIcons[i % approachIcons.length];
              return (
                <article
                  key={a.title}
                  className="rounded-2xl bg-white border border-ink-10 p-6 soft-card hover:-translate-y-0.5 transition-transform duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FCE7D6] to-[#F8D2B0] text-[#C2603A] flex items-center justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="serif-display text-2xl mt-5">{a.title}</h3>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-muted">{a.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>Formats</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">Training Formats</h2>
            <p className="mt-4 text-muted max-w-2xl mx-auto">Mix and match based on your team&apos;s needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            {trainingFormats.map((f, i) => {
              const Icon = formatIcons[i];
              return (
                <article
                  key={f.title}
                  className="rounded-2xl bg-[#FBF5EC] border border-ink-10 p-6 md:p-7 soft-card hover:-translate-y-0.5 transition-transform duration-300"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white border border-ink-10 flex items-center justify-center text-[#C2603A]">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      <span className="px-2.5 py-1 rounded-full bg-[#FCE7D6] text-[#8a3a1c] border border-[#C2603A]/20 uppercase tracking-wide">
                        {f.duration}
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-white border border-ink-10 text-muted">
                        {f.audience}
                      </span>
                    </div>
                  </div>
                  <h3 className="serif-display text-2xl md:text-3xl mt-5">{f.title}</h3>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-muted">{f.description}</p>
                  <div className="mt-5 pt-4 border-t border-ink-10 flex items-start gap-2 text-[13.5px] text-ink/85">
                    <Check className="w-4 h-4 mt-0.5 text-[#C2603A] shrink-0" />
                    <span>{f.outcome}</span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="relative py-20 md:py-28 bg-[#F6EFE4]">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>Curriculum</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">What Your Team Will Learn</h2>
            <p className="mt-4 text-muted max-w-2xl mx-auto">
              No buzzwords. Just practical skills they can use immediately.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            <div className="rounded-2xl bg-white border border-ink-10 p-6 md:p-7 soft-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FCE7D6] text-[#C2603A] flex items-center justify-center">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <h3 className="serif-display text-2xl">For Everyone</h3>
              </div>
              <ul className="mt-5 space-y-3">
                {curriculumCore.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-[14.5px] text-ink/85">
                    <span className="mt-1 w-5 h-5 rounded-full bg-[#FCE7D6] text-[#C2603A] flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-ink-10 p-6 md:p-7 soft-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FCE7D6] text-[#C2603A] flex items-center justify-center">
                  <Target className="w-5 h-5" />
                </div>
                <h3 className="serif-display text-2xl">Role-Specific Examples</h3>
              </div>
              <ul className="mt-5 space-y-3">
                {curriculumRoles.map((r) => (
                  <li key={r.role} className="flex items-start gap-3 text-[14.5px] text-ink/85">
                    <span className="text-[12px] font-semibold text-[#C2603A] uppercase tracking-wider min-w-[88px] mt-0.5">
                      {r.role}
                    </span>
                    <span className="text-muted">{r.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>Who It&apos;s For</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">Who This Is For</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {audienceFit.map((a) => (
              <article key={a.title} className="rounded-2xl bg-[#FBF5EC] border border-ink-10 p-6 soft-card flex flex-col">
                <h3 className="serif-display text-2xl">{a.title}</h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-muted flex-1">{a.description}</p>
                <div className="mt-5 inline-flex items-center self-start gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[#C2603A] bg-[#FCE7D6]/60 border border-[#C2603A]/20 rounded-full px-3 py-1">
                  <Check className="w-3 h-3" /> Perfect Fit
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-20 md:py-28 bg-[#F6EFE4]">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <SectionEyebrow>Process</SectionEyebrow>
            <h2 className="serif-display text-4xl md:text-5xl mt-4">How It Works</h2>
            <p className="mt-4 text-muted max-w-2xl mx-auto">
              From assessment to certification in 4 steps
            </p>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-4 gap-5 mt-12">
            {trainingProcess.map((s, i) => (
              <div key={s.number} className="relative">
                <article className="rounded-2xl bg-white border border-ink-10 p-6 soft-card border-l-4 border-l-[#C2603A] h-full">
                  <div className="text-[#C2603A] font-semibold tracking-wide text-sm">{s.number}</div>
                  <h3 className="serif-display text-2xl mt-2">{s.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-muted">{s.description}</p>
                  <div className="mt-4 inline-block text-[11px] tracking-[0.12em] uppercase text-[#C2603A] bg-[#FCE7D6]/60 border border-[#C2603A]/20 rounded-full px-3 py-1">
                    {s.meta}
                  </div>
                </article>
                {i < trainingProcess.length - 1 && (
                  <span className="hidden md:block absolute top-12 -right-2.5 w-5 h-5 rotate-45 border-t-2 border-r-2 border-[#C2603A]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="relative mx-auto max-w-4xl px-5 text-center">
          <h2 className="serif-display text-4xl md:text-5xl">Ready to Upskill Your Team?</h2>
          <p className="mt-4 text-muted max-w-2xl mx-auto">
            Let&apos;s design a custom training program that actually works.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/#contact"
              className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-medium"
            >
              Design Your Training <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#services"
              className="btn-outline inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-medium"
            >
              See Transformation Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Education;
